/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { applyParameters, fetchAsJson, fetchAsCSV } from '@modules/query/helpers'

import type { operations, components } from '../ccm-admin-api/v1/types'

type AdminListClustersHeaderParameters = operations['adminListClusters']['parameters']['header']
type AdminListClustersQueryParameters = operations['adminListClusters']['parameters']['query']

const ACCEPT_HEADER = 'Accept'
const ELASTIC_PAGE_SIZE_HEADER = 'X-Elastic-Page-Size'
const ELASTIC_NEXT_PAGE_HEADER = 'X-Elastic-Next-Page'

const getHeadersFromArgs = (
  args?: {
    headerParameters?: AdminListClustersHeaderParameters
  },
  extraHeaders?: Record<string, string>,
): Record<string, string> => {
  const headers: Record<string, string> = { ...(extraHeaders ?? {}) }
  const hp = args?.headerParameters

  if (hp?.[ELASTIC_PAGE_SIZE_HEADER] !== undefined) {
    headers[ELASTIC_PAGE_SIZE_HEADER] = String(hp[ELASTIC_PAGE_SIZE_HEADER])
  }

  if (hp?.[ELASTIC_NEXT_PAGE_HEADER] !== undefined) {
    headers[ELASTIC_NEXT_PAGE_HEADER] = hp[ELASTIC_NEXT_PAGE_HEADER] as string
  }

  return headers
}

export async function* exportClusterList(queryParameters: AdminListClustersQueryParameters) {
  const PAGE_SIZE = 100
  let nextToken: string | null = null

  do {
    const headerParameters: AdminListClustersHeaderParameters = {
      'X-Elastic-Page-Size': PAGE_SIZE,
      ...(nextToken ? { 'X-Elastic-Next-Page': nextToken } : {}),
    }

    const { text: csvText, nextToken: headerNext } = await exportClusterListWithHeaders({
      queryParameters,
      headerParameters,
    })

    yield csvText

    nextToken = headerNext
  } while (nextToken)
}

// GET /api/v1/admin/cloud-connected/clusters (CSV) with header pagination support
const exportClusterListWithHeaders = async (args?: {
  queryParameters?: AdminListClustersQueryParameters
  headerParameters?: AdminListClustersHeaderParameters
}): Promise<{ text: string; nextToken: string | null }> => {
  const url = applyParameters('/api/v1/admin/cloud-connected/clusters', args)
  const headers: Record<string, string> = getHeadersFromArgs(args, { [ACCEPT_HEADER]: 'text/csv' })

  const { text, headers: responseHeaders } = await fetchAsCSV(url, { method: 'GET', headers })
  const nextToken = responseHeaders.get(ELASTIC_NEXT_PAGE_HEADER)
  return { text, nextToken }
}

// GET /api/v1/admin/cloud-connected/clusters with header pagination support
export const listClustersWithHeaders = async (args?: {
  queryParameters?: AdminListClustersQueryParameters
  headerParameters?: AdminListClustersHeaderParameters
}) =>
  fetchAsJson<components['schemas']['ClusterList']>(
    applyParameters('/api/v1/admin/cloud-connected/clusters', args),
    {
      method: 'GET',
      headers: getHeadersFromArgs(args),
    },
  )
